/**
 * CalendarSyncButtons
 * -------------------
 * One-click calendar subscription buttons for staff schedule sync.
 *
 * Replaces the legacy "copy this URL into your calendar app" workflow with
 * platform-specific deep links:
 *   - Google Calendar  → opens Google's "Add by URL" page with the .ics URL pre-filled
 *   - Apple Calendar   → opens webcal:// URL (macOS / iOS prompts to subscribe)
 *   - Outlook          → opens webcal:// URL (Outlook desktop/web detects subscription)
 *   - Copy Link        → manual fallback (kept for parity)
 *
 * Props:
 *   calendarUrl (string, required) — full https:// .ics URL
 *
 * Backend untouched. This is purely a UX upgrade on top of the existing
 * .ics endpoint.
 */

import React, { useMemo, useState } from 'react';
import { Link2, Check, ChevronDown, ChevronUp, CalendarDays } from 'lucide-react';
import { employeeAPI } from '../lib/api';

// ---------- Brand icons (inline SVG, no dependency) ----------

const GoogleCalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <rect x="6" y="6" width="36" height="36" rx="4" fill="#fff" stroke="#e0e0e0" />
    <path fill="#1a73e8" d="M30 18h-12v12h12V18z" />
    <path fill="#ea4335" d="M30 6v6h6l-6-6z" />
    <path fill="#fbbc04" d="M36 12h6v6h-6z" />
    <path fill="#34a853" d="M30 36h6v6l-6-6z" />
    <path fill="#4285f4" d="M36 30h6v6h-6z" />
    <text x="24" y="28" fontFamily="Arial" fontSize="10" fontWeight="700" textAnchor="middle" fill="#1a73e8">31</text>
  </svg>
);

const AppleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const OutlookIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#0078d4" d="M28 6h14a2 2 0 0 1 2 2v32a2 2 0 0 1-2 2H28V6z"/>
    <path fill="#1490df" d="M28 14h16v10H28z"/>
    <path fill="#28a8ea" d="M28 24h16v10H28z"/>
    <path fill="#0078d4" d="M4 10l22-4v36L4 38V10z"/>
    <circle cx="15" cy="24" r="6" fill="#fff"/>
    <circle cx="15" cy="24" r="3" fill="#0078d4"/>
  </svg>
);

// ---------- URL helpers ----------

function toWebcal(httpsUrl) {
  if (!httpsUrl) return '';
  return httpsUrl.replace(/^https?:/i, 'webcal:');
}

function googleAddByUrl(calendarUrl) {
  return `https://calendar.google.com/calendar/u/0/r/settings/addbyurl?cid=${encodeURIComponent(calendarUrl)}`;
}

// ---------- Component ----------

export default function CalendarSyncButtons({ calendarUrl }) {
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const webcalUrl = useMemo(() => toWebcal(calendarUrl), [calendarUrl]);
  const googleUrl = useMemo(() => googleAddByUrl(calendarUrl), [calendarUrl]);

  // Mark the calendar-sync onboarding step complete on any sync action.
  // Idempotent on the backend — first-write wins.
  const markSynced = () => {
    employeeAPI.markOnboardingEvent('calendar_sync').catch(() => {});
  };

  const handleCopy = () => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(calendarUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      markSynced();
    });
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    markSynced();
  };

  const subscribeWebcal = () => {
    window.location.href = webcalUrl;
    markSynced();
  };

  const baseBtn =
    'inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-150 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500 ' +
    'min-h-[44px] w-full sm:w-auto'; // 44px = iOS minimum tap target

  return (
    <div
      className="bg-white rounded-xl border border-orange-200 shadow-sm p-5"
      data-testid="calendar-sync-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <CalendarDays size={20} className="text-orange-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 text-base" data-testid="calendar-sync-title">
            Sync Your Work Schedule
          </h3>
          <p className="text-slate-500 text-sm mt-1" data-testid="calendar-sync-subtitle">
            Subscribe once. Your shifts automatically appear and stay updated.
          </p>
        </div>
      </div>

      {/* Buttons — horizontal on desktop, stacked on mobile */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-2">
        <button
          type="button"
          onClick={() => openInNewTab(googleUrl)}
          aria-label="Subscribe in Google Calendar"
          data-testid="calendar-sync-google-btn"
          className={`${baseBtn} bg-orange-500 hover:bg-orange-600 text-white shadow-sm`}
        >
          <GoogleCalendarIcon size={18} />
          <span>Google Calendar</span>
        </button>

        <button
          type="button"
          onClick={subscribeWebcal}
          aria-label="Subscribe in Apple Calendar"
          data-testid="calendar-sync-apple-btn"
          className={`${baseBtn} bg-slate-900 hover:bg-slate-800 text-white shadow-sm`}
        >
          <AppleIcon size={18} />
          <span>Apple Calendar</span>
        </button>

        <button
          type="button"
          onClick={subscribeWebcal}
          aria-label="Subscribe in Outlook"
          data-testid="calendar-sync-outlook-btn"
          className={`${baseBtn} bg-[#0078d4] hover:bg-[#106ebe] text-white shadow-sm`}
        >
          <OutlookIcon size={18} />
          <span>Outlook</span>
        </button>

        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? 'Calendar link copied' : 'Copy calendar link'}
          data-testid="calendar-sync-copy-btn"
          className={`${baseBtn} border ${copied ? 'border-green-300 bg-green-50 text-green-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
        >
          {copied ? <Check size={16} /> : <Link2 size={16} />}
          <span>{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
      </div>

      {/* Advanced (collapsed by default) */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 focus:outline-none focus-visible:underline"
          aria-expanded={showAdvanced}
          aria-controls="calendar-sync-advanced"
          data-testid="calendar-sync-advanced-toggle"
        >
          {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Advanced options
        </button>
        {showAdvanced && (
          <div
            id="calendar-sync-advanced"
            className="mt-2 bg-slate-50 rounded-lg border border-slate-200 px-3 py-2.5"
            data-testid="calendar-sync-advanced-panel"
          >
            <p className="text-xs text-slate-500 mb-1">Raw subscription URL (for manual setup):</p>
            <code
              className="block text-xs text-slate-700 font-mono break-all select-all"
              data-testid="calendar-sync-raw-url"
            >
              {calendarUrl}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}
